/**
 * Edpies — 16-week Learning Plan task router.
 *
 * The 16-week plan emits weekly tasks with shapes like:
 *   { id, type: 'revision'|'practice'|'quiz', subject, topic, difficulty, ... }
 *
 * When the student taps "Start Task" we want to land them on the **specific**
 * learning surface for that subject + topic — not a generic Resource Hub or
 * Growth Zone landing. This module turns a task into a concrete navigation
 * target, age-group aware (7+/11+/Year 9/GCSE).
 */

// Map a user record (or year_group string) → backend stage slug used by
// resources, papers and challenger routes.
export const resolveStageSlug = (userOrYearGroup) => {
  const u = userOrYearGroup || {};
  const yg = (
    (typeof u === 'string' ? u : u.year_group || u.yearGroup || u?.studentProfile?.yearGroup) || ''
  ).toLowerCase();
  const examGoal = (
    (typeof u === 'string' ? '' : u?.studentProfile?.examGoal || u?.examFocus || '')
  ).toLowerCase();
  if (/year[\s-]?1[01]\b/.test(yg) || examGoal === 'gcse') return 'gcse';
  if (/year[\s-]?[789]\b/.test(yg)) return 'year-9';
  if (/year[\s-]?[12]\b/.test(yg) || examGoal === '7+' || examGoal === '7plus') return '7-plus';
  return '11-plus';
};

// Normalise a freeform topic label ("Coding", "Number & Place Value",
// "Algebra: Linear Equations") to a kebab-cased slug suitable for the
// /resources/:subject/:topic route. Keeps existing slugs untouched.
export const slugifyTopic = (raw) => {
  if (!raw) return '';
  return String(raw)
    .toLowerCase()
    .replace(/[&]/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Subject normalisation — backend uses 'maths' / 'english' / 'verbal-reasoning'
// / 'non-verbal-reasoning' for resource paths.
export const subjectSlug = (raw) => {
  const s = (raw || '').toLowerCase().replace(/_/g, '-');
  if (s.startsWith('verbal')) return 'verbal-reasoning';
  if (s.startsWith('non') || s.includes('nvr')) return 'non-verbal-reasoning';
  if (s.startsWith('eng')) return 'english';
  if (s.startsWith('math')) return 'maths';
  if (s.startsWith('sci') || s === 'biology' || s === 'chemistry' || s === 'physics') return s.startsWith('sci') ? 'science' : s;
  return s || 'maths';
};

/**
 * Resolve a task → { path, label }.
 *
 * Routing decisions:
 *   revision  → Resource Topic page (Focus Mode reading + worked example)
 *   practice  → Practice Paper filtered by topic (best available paper)
 *   quiz      → Challenger Mode for that subject (timed, gamified)
 *
 * Returns `path` (react-router target) and `label` (button copy override).
 */
export const resolveTaskTarget = (task, user) => {
  if (!task) return { path: '/dashboard', label: 'Open Dashboard' };
  const subject = subjectSlug(task.subject);
  const topic = slugifyTopic(task.topic || task.title) || 'general';
  // Stage is resolved inside LearningTask via the user profile so we don't
  // bake it into the URL. Type drives the headline copy + XP reward only.
  switch (task.type) {
    case 'practice':
      return { path: `/learn/practice/${subject}/${topic}`, label: 'Start Practice' };
    case 'quiz':
      return { path: `/learn/quiz/${subject}/${topic}`, label: 'Take the Challenge' };
    case 'revision':
    default:
      return { path: `/learn/revision/${subject}/${topic}`, label: 'Start Revision' };
  }
  // (stage / user kept available for future deep links — currently unused)
};
